import type { Optional } from '@festina-life/core'
import { useLocalStorage } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import type { TemplateVersion } from '../types/templateModel'
import { useTemplateVersion } from '../uiModels/useTemplateVersion'
import { useTemplateVersionInfos } from '../uiModels/versionInfo/useTemplateVersionInfo'
import { usePreviousTemplateVersionInfo } from './usePreviousTemplateVersionInfo'

export const useSelectedPreviousVersion = (
  templateVersion: Optional<TemplateVersion>,
) => {
  const [
    selectedPreviousVersionForDiffingOperationKey,
    setSelectedPreviousVersionForDiffingOperationKey,
  ] = useLocalStorage<string>(
    `previous-diff-operationKey-${templateVersion?.versionInfo.operationKey}`,
  )
  const allVersionInfosForTemplate = useTemplateVersionInfos(
    templateVersion?.versionInfo.identifier,
  )
  const selectedPreviousVersionForDiffing = useMemo(
    () =>
      allVersionInfosForTemplate.find(
        (versionInfo) =>
          versionInfo.operationKey ===
          selectedPreviousVersionForDiffingOperationKey,
      ),
    [allVersionInfosForTemplate, selectedPreviousVersionForDiffingOperationKey],
  )

  const previousVersionInfo = usePreviousTemplateVersionInfo(
    templateVersion?.versionInfo,
  )

  const previousVersion = useTemplateVersion({
    versionInfo: selectedPreviousVersionForDiffing ?? previousVersionInfo,
    // These versions are generated for diffing purposes. Initially we only diff against non-flattened templates.
    isFlattened: false,
  })

  return {
    previousVersion,
    setSelectedPreviousVersionForDiffingOperationKey,
    isDiffingAgainstShadowedVersion:
      previousVersionInfo?.operationKey !==
      previousVersion?.versionInfo.operationKey,
  }
}
